import { useEffect, useMemo, useState } from 'react';
import useGroupSettingsStore from '@modules/digital-signage/components/group-settings/store/group-settings.store.ts';
import { UseGroupSettingsCloseParams } from '@modules/digital-signage/model/group-settings.model.ts';
import { hasScheduleChanges } from '@modules/digital-signage/utils/schedule-form.utils.ts';
import { useUnsavedChangesGuard } from '@modules/digital-signage/components/group-settings/hooks/use-unsaved-changes-guard.ts';

export const useGroupSettingsClose = ({
  isDirty,
  hasOpenDialog,
  onClose
}: UseGroupSettingsCloseParams) => {
  const { schedules, savedSchedules, isLoading } = useGroupSettingsStore();
  const [isCloseRequested, setIsCloseRequested] = useState<boolean>(false);

  const schedulesChanged = useMemo(
    () => hasScheduleChanges(schedules, savedSchedules),
    [schedules, savedSchedules]
  );
  const hasChanges = isDirty || schedulesChanged;
  const blocker = useUnsavedChangesGuard(hasChanges);
  const isNavigationBlocked = blocker.state === 'blocked';
  const showUnsavedDialog = isCloseRequested || isNavigationBlocked;

  const closeUnsavedDialog = () => {
    setIsCloseRequested(false);
    blocker.reset?.();
  };

  const requestClose = () => {
    if (!hasChanges) {
      onClose();
      return;
    }
    if (!isLoading()) setIsCloseRequested(true);
  };

  const handleDiscard = () => {
    if (isNavigationBlocked) {
      blocker.proceed?.();
      return;
    }
    setIsCloseRequested(false);
    onClose();
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !hasOpenDialog && !showUnsavedDialog) {
        requestClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [requestClose, hasOpenDialog, showUnsavedDialog]);

  return {
    blocker,
    hasChanges,
    showUnsavedDialog,
    requestClose,
    closeUnsavedDialog,
    handleDiscard
  };
};
